import * as React from "react";
import JobTags from "./JobTags";

interface JobProps {
  job: any;
  isZipJob?: boolean;
}

export const Job: React.FC<JobProps> = ({ job, isZipJob = false }) => {
  const handleClick = () => {
    window.open(job.url, "_blank");
  };

  const urgent = job.spotlight || job.priority;

  return (
    <article
      className="job-card"
      onClick={handleClick}
      style={{
        cursor: 'pointer',
        display: 'flex',
        gap: '1.25rem',
        padding: '1.5rem',
        borderBottom: '1px solid #f0f2f5',
        transition: 'all 0.2s ease',
        position: 'relative',
        backgroundColor: 'white',
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.backgroundColor = '#fafbfc';
        e.currentTarget.style.transform = 'translateX(4px)';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.backgroundColor = 'white';
        e.currentTarget.style.transform = 'translateX(0)';
      }}
    >
      {/* Accent line on hover */}
      <div
        className="job-card__accent"
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '3px',
          background: urgent
            ? 'linear-gradient(180deg, #e74c3c, #c0392b)'
            : 'linear-gradient(180deg, #3498db, #2980b9)',
          opacity: 0,
          transition: 'opacity 0.2s ease',
        }}
      />

      {/* Company Logo */}
      <div style={{ flexShrink: 0 }}>
        <a
          href={job.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{ display: 'block' }}
        >
          <img
            src={job.image}
            alt={job.practice_name || 'Company logo'}
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '12px',
              objectFit: 'cover',
              border: '2px solid #f0f2f5',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = '#3498db';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = '#f0f2f5';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          />
        </a>
      </div>

      {/* Job Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Header Row: Title + Badges */}
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.75rem',
          marginBottom: '0.5rem',
          flexWrap: 'wrap',
        }}>
          <h3 style={{
            margin: 0,
            fontSize: '1.0625rem',
            fontWeight: 600,
            color: '#1a365d',
            lineHeight: 1.4,
            letterSpacing: '-0.01em',
          }}>
            {job.title}
          </h3>

          {/* Badges */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {urgent && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.25rem 0.625rem',
                borderRadius: '100px',
                fontSize: '0.6875rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
                background: 'linear-gradient(135deg, #fee2e2, #fecaca)',
                color: '#dc2626',
                border: '1px solid #fca5a5',
              }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13 3v10h5l-6 8v-10H7l6-8z"/>
                </svg>
                Urgent
              </span>
            )}

            {isZipJob && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.25rem 0.625rem',
                borderRadius: '100px',
                fontSize: '0.6875rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
                background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                color: '#b45309',
                border: '1px solid #fcd34d',
              }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                Featured
              </span>
            )}
          </div>
        </div>

        {/* Meta Row: Company, Location, Salary - matching profile attribute styling exactly */}
        <div className="col-12 d-flex flex-wrap">
          <span className="profile-attribute-bubble rounded px-2 py-1 me-1 mb-1 d-md-none col-12">
            <i className="fa fa-building"></i> {job.practice_name}
          </span>
          <span className="profile-attribute-bubble rounded px-2 py-1 me-1 mb-1 d-none d-md-block">
            <i className="fa fa-building"></i> {job.practice_name}
          </span>

          <span className="profile-attribute-bubble rounded px-2 py-1 me-1 mb-1 d-md-none col-12">
            <i className="fa fa-map-marker"></i> {job.practice_city}, {job.practice_state}
          </span>
          <span className="profile-attribute-bubble rounded px-2 py-1 me-1 mb-1 d-none d-md-block">
            <i className="fa fa-map-marker"></i> {job.practice_city}, {job.practice_state}
          </span>

          {job.compensation && (
            <>
              <span className="profile-attribute-bubble rounded px-2 py-1 me-1 mb-1 d-md-none col-12">
                <i className="fa fa-money"></i> {job.compensation}
              </span>
              <span className="profile-attribute-bubble rounded px-2 py-1 me-1 mb-1 d-none d-md-block">
                <i className="fa fa-money"></i> {job.compensation}
              </span>
            </>
          )}
        </div>

        {/* Tags Row */}
        {!isZipJob && (
          <div style={{ marginTop: '0.5rem' }}>
            <JobTags job={job} />
          </div>
        )}

        {/* ZipRecruiter Attribution */}
        {isZipJob && (
          <a
            href="https://www.ziprecruiter.com/jobs"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.375rem',
              marginTop: '0.75rem',
              textDecoration: 'none',
              color: '#94a3b8',
              fontSize: '0.75rem',
              transition: 'color 0.2s ease',
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseOver={(e) => e.currentTarget.style.color = '#64748b'}
            onMouseOut={(e) => e.currentTarget.style.color = '#94a3b8'}
          >
            <span>Job Search by</span>
            <img
              src="https://www.ziprecruiter.com/img/logos/logo-sm-black-304px.png"
              alt="ZipRecruiter"
              style={{ height: '16px', opacity: 0.7 }}
            />
          </a>
        )}
      </div>

      {/* Arrow indicator */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        color: '#cbd5e1',
        transition: 'all 0.2s ease',
        opacity: 0,
      }}
      className="job-card__arrow"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </div>

      <style>{`
        .job-card:hover .job-card__accent {
          opacity: 1 !important;
        }
        .job-card:hover .job-card__arrow {
          opacity: 1 !important;
          color: #3498db !important;
          transform: translateX(4px);
        }
        @media (max-width: 640px) {
          .job-card {
            flex-direction: column !important;
            gap: 1rem !important;
            padding: 1.25rem !important;
          }
          .job-card__arrow {
            display: none !important;
          }
        }
      `}</style>
    </article>
  );
};

export default Job;
